import { readWithRecovery } from '../utils/loadWithRecovery';
import { appLocalStorage } from '../utils/storage';
import axios from 'axios';
import config from '../utils/config';
import { requestLegalConsent } from '../legal/consent';

const baseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
export const mediaUrl = value => value && /^\//.test(value) ? `${baseUrl}${value}` : value;
export const MEDIA_DISABLED = Object.freeze({
  audio: { enabled: false, max_count: 10, max_duration_seconds: 300 },
  video: { enabled: false, max_count: 5, max_duration_seconds: 60 },
  available: false,
});

function request(path, options = {}) {
  const authToken = options.authToken === undefined ? appLocalStorage.getItem(config.token) : options.authToken;
  // Playback authorization is an idempotent read, although its route uses POST.
  return !options.method || options.method === 'GET' || path.endsWith('/playback')
    ? readWithRecovery(attempt => requestOnce(path, { ...options, authToken, ...attempt }), { signal: options.signal })
    : requestOnce(path, options);
}

async function requestOnce(path, { method = 'GET', data, signal, onUploadProgress, binary = false, authToken, timeout = 60000 } = {}) {
  const token = authToken === undefined ? appLocalStorage.getItem(config.token) : authToken;
  if (authToken !== undefined && token !== appLocalStorage.getItem(config.token)) throw new Error('MEDIA_ACCOUNT_CHANGED');
  const options = { url: `${baseUrl}/media${path}`, method, data, signal,
    timeout, onUploadProgress,
    headers: { ...(token ? { [config.token]: token } : {}),
      [config.lng]: appLocalStorage.getItem('i18nextLng') || 'en',
      ...(binary ? { 'Content-Type': 'application/octet-stream' } : {}) },
  };
  let response;
  try { response = await axios(options); }
  catch (error) {
    if (error.response?.status !== 428 || error.response?.data?.error?.code !== 'LEGAL_CONSENT_REQUIRED') throw error;
    if (!await requestLegalConsent(error.response.data.legal, token, signal) || signal?.aborted || token !== appLocalStorage.getItem(config.token)) throw cancelled();
    response = await axios(options);
  }
  // Ignore responses from a previous session, including mocked/non-abortable
  // transports that resolve after their caller has already left the page.
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
  if (token !== appLocalStorage.getItem(config.token)) throw new Error('MEDIA_ACCOUNT_CHANGED');
  if (response.data?.success === false) throw new Error(response.data.message || 'MEDIA_REQUEST_FAILED');
  return response.data;
}

export async function getMediaCapabilities(signal) {
  try {
    const result = await request('/capabilities', { signal });
    const value = result.capabilities || result;
    const caps = { ...MEDIA_DISABLED, ...value, available: true,
      audio: { ...MEDIA_DISABLED.audio, ...value.audio, enabled: value.audio?.enabled === true },
      video: { ...MEDIA_DISABLED.video, ...value.video, enabled: value.video?.enabled === true } };
    window.dispatchEvent(new CustomEvent('wiw-media-capabilities', { detail: caps }));
    return caps;
  } catch {
    // Old deployments and offline browsers cannot authorize new captures.
    return MEDIA_DISABLED;
  }
}

export const getMedia = (id, signal, authToken) => request(`/${encodeURIComponent(id)}`, { signal, authToken });
export const deleteMedia = (id, signal) => request(`/${encodeURIComponent(id)}`, { method: 'DELETE', signal });
export async function requestMediaPlayback(id, reactionId, signal) {
  const result = await request(`/${encodeURIComponent(id)}/playback`, {
    method: 'POST', data: reactionId ? { reaction_id: reactionId } : {}, signal,
  });
  return { ...result, playback_url: mediaUrl(result.playback_url || result.url),
    poster_url: mediaUrl(result.poster_url), waveform_url: mediaUrl(result.waveform_url) };
}

const mediaOf = response => response.media || response;
const uploadProgress = (offset, total, update) => event => update({ progress: (offset + event.loaded) / total });
const cancelled = () => new DOMException('Aborted', 'AbortError');
const pause = (ms, signal) => new Promise((resolve, reject) => {
  if (signal?.aborted) { reject(cancelled()); return; }
  const abort = () => { clearTimeout(timer); reject(cancelled()); };
  const timer = setTimeout(() => { signal?.removeEventListener('abort', abort); resolve(); }, ms);
  signal?.addEventListener('abort', abort, { once: true });
});

// Each accepted ID and offset is persisted by the caller, so retries resume the
// same upload. Completing the upload never publishes an opinion.
export async function uploadRecording(item, { storyId, draftId, caps, signal, update }) {
  const authToken = appLocalStorage.getItem(config.token);
  let response;
  let mediaId = item.id;
  if (mediaId) response = await getMedia(mediaId, signal, authToken);
  else {
    if (caps[item.kind]?.max_bytes && item.blob.size > caps[item.kind].max_bytes) throw new Error('MEDIA_FILE_TOO_LARGE');
    response = await request('/uploads', { method: 'POST', signal, authToken, data: {
      kind: item.kind, story_id: Number(storyId), draft_id: draftId,
      bytes: item.blob.size, mime_type: item.blob.type, duration_ms: item.duration_ms,
      ...(item.replaces_id ? { replaces_id: item.replaces_id } : {}),
    } });
    mediaId = mediaOf(response).id;
    if (!mediaId) throw new Error('MEDIA_UPLOAD_INVALID');
    await update({ ...mediaOf(response), id: mediaId });
  }
  let current = mediaOf(response);
  if (current.status === 'uploading') {
    if (!item.blob) throw new Error('MEDIA_LOCAL_BLOB_MISSING');
    const chunkSize = Number(response.chunk_bytes || caps.upload_chunk_bytes);
    if (!Number.isSafeInteger(chunkSize) || chunkSize <= 0) throw new Error('MEDIA_UPLOAD_UNAVAILABLE');
    let offset = Number(response.offset || 0);
    while (offset < item.blob.size) {
      if (signal?.aborted) throw cancelled();
      const part = item.blob.slice(offset, offset + chunkSize);
      const result = await request(`/${encodeURIComponent(mediaId)}/content?offset=${offset}`, {
        method: 'PUT', binary: true, signal, authToken, data: part,
        onUploadProgress: uploadProgress(offset, item.blob.size, update),
      });
      const next = Number(result.offset);
      if (!Number.isSafeInteger(next) || next <= offset || next > item.blob.size) throw new Error('MEDIA_UPLOAD_OFFSET');
      offset = next;
      await update({ progress: offset / item.blob.size });
    }
    response = await request(`/${encodeURIComponent(mediaId)}/complete`, { method: 'POST', signal, authToken });
    current = mediaOf(response);
  }
  await update(current);
  while (['queued', 'processing'].includes(current.status)) {
    await pause(2000, signal);
    current = mediaOf(await getMedia(mediaId, signal, authToken));
    await update(current);
  }
  if (current.status !== 'ready') throw new Error(current.error_code || 'MEDIA_PROCESSING_FAILED');
  return current;
}
